import { Filter } from './types';
import { Range, RangeBounds } from './search-panel-types';
import { escapeSearchSpecialCharacters } from './query-formatters';

export const AuthorsFilter = (authors: string): Filter => {
  const trimmedAuthors = authors.trim();
  return trimmedAuthors ? { 'authors.fullName': escapeSearchSpecialCharacters(trimmedAuthors) } : {};
};

export const SerieFilter = (serieId: string): Filter => ({ 'serie.id': serieId });

export const OriginalTitleFilter = (originalTitle: string): Filter => {
  const trimmedOriginalTitle = originalTitle.trim();
  return trimmedOriginalTitle ? { originalTitle: escapeSearchSpecialCharacters(trimmedOriginalTitle) } : {};
};

export const LanguageTextFilter = (languages: string): Filter => ({ 'languages.text': languages });

export const LanguageAudioFilter = (languages: string): Filter => ({ 'languages.audio': languages });

export const LanguageVideoFilter = (languages: string): Filter => ({ 'languages.video': languages });

export const AgeFilter = (min: number, max: number): Filter => ({
  ages: `age(${Number.isFinite(min) ? min : 0}, ${Number.isFinite(max) ? max : 99})`,
});

/** The filter value of a range: 'age(3, 10)' for ages, '[60 TO 300]' for other fields */
export const getRangeFilterValue = (fieldKey: string, { min, max }: RangeBounds): string =>
  fieldKey === 'ages' ? `age(${min}, ${max})` : `[${min} TO ${max}]`;

/** Reads the bounds back from a range filter value, e.g. '[60 TO 300]' or 'age(60, 300)' → { min: 60, max: 300 } */
export const getRangeBounds = (filterValue: string): RangeBounds | undefined => {
  const range_pattern = /^\[(\d+) TO (\d+)\]$/; // e.g. '[60 TO 300]'
  const age_range_pattern = /^age\((\d+), (\d+)\)$/; // e.g. 'age(3, 10)'

  const match = range_pattern.exec(filterValue) ?? age_range_pattern.exec(filterValue);
  if (!match) {
    return undefined;
  }
  const [, min, max] = match;
  return { min: Number(min), max: Number(max) };
};

/**
 * The predefined range a selected range was made from. The last range is often open-ended (max: MAX_SAFE_INTEGER,
 * shown as e.g. '60+ min'), and is stored with the data max at the time of selection, e.g. [3600 TO 16140].
 */
const findPredefinedRange = (ranges: Range[] = [], { min, max }: RangeBounds): Range | undefined =>
  ranges.find((range) => range.min === min && (range.max === max || range.max === Number.MAX_SAFE_INTEGER));

export const isOpenEndedRange = (ranges: Range[] = [], bounds: RangeBounds): boolean =>
  findPredefinedRange(ranges, bounds)?.max === Number.MAX_SAFE_INTEGER;

export const CategoryFilter = (categoryId: string): Filter => ({ categories: categoryId });

export const SubscriptionTypeFilter = (type: string | string[]): Filter => ({
  'accessRules.subscriptionType': type,
});

export const RealmFilter = (realm: string): Filter => ({ realm });

export const AvailabilityRealmsFilter = (realm: string): Filter => ({ availabilityRealms: realm });

export const RoleFilter = (role: string): Filter => ({
  'accessRules.roles': role,
});
